import React from 'react';

import { useIntl } from 'react-intl-next';

import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import {
	AI_MENU_SECTION,
	MAIN_BLOCK_MENU_SECTION_RANK,
	AI_NESTED_MENU_KEY,
	AI_NESTED_SECTION_KEY,
	AI_MENU_SECTION_RANK,
} from '@atlaskit/editor-common/block-menu';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { RegisterBlockMenuComponent } from '@atlaskit/editor-plugin-block-menu';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { ToolbarDropdownItemSection, ToolbarNestedDropdownMenu } from '@atlaskit/editor-toolbar';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';

import type { AIPlugin } from '../../aiPluginType';
import type { EditorPluginAIProvider } from '../../types/types';

import { getBlockMenuActions } from './actions';
import { BlockMenuActionItem } from './BlockMenuActionItem';
import type { BlockMenuActionDef, BlockMenuSelectionContext } from './types';

/**
 * Show more - nested menu trigger.
 */
const ShowMoreMenu = ({ children }: { children: React.ReactNode }): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<ToolbarNestedDropdownMenu
			text={formatMessage(aiMessages.blockMenuNestedMenuTitle)}
			elemBefore={<ShowMoreHorizontalIcon label="" size="small" />}
			elemAfter={<ChevronRightIcon label="" size="small" />}
			shouldFitContainer
		>
			{children}
		</ToolbarNestedDropdownMenu>
	);
};

/**
 * Generic nested action menu trigger for parent actions with children (e.g., "Change tone", "Translate").
 */
const NestedActionMenu = ({
	action,
	children,
}: {
	action: BlockMenuActionDef;
	children: React.ReactNode;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<ToolbarNestedDropdownMenu
			text={formatMessage(action.label)}
			elemBefore={action.elemBefore}
			elemAfter={<ChevronRightIcon label="" size="small" />}
		>
			{children}
		</ToolbarNestedDropdownMenu>
	);
};

/**
 * Builds the selection context from the block that triggered the menu.
 *
 * Uses `blockControls` shared state to find the triggering node position,
 * then resolves the node from the editor state to extract block type and text length.
 */
const getSelectionContext = ({
	api,
	getEditorView,
}: {
	api: ExtractInjectionAPI<AIPlugin>;
	getEditorView: () => EditorView | undefined;
}): BlockMenuSelectionContext | null => {
	const editorView = getEditorView();
	if (!editorView) {
		return null;
	}

	const blockControlsState = api?.blockControls?.sharedState.currentState();
	const pos = blockControlsState?.menuTriggerByNode?.pos;
	if (pos === null || pos === undefined) {
		return null;
	}

	const node = editorView.state.doc.nodeAt(pos);
	if (!node) {
		return null;
	}

	return {
		blockType: node.type.name,
		textLength: node.textContent.length,
	};
};

/**
 * Determines if an action should be hidden from a given menu location.
 *
 * | Action config                        | Hidden from primary | Hidden from nested |
 * |--------------------------------------|---------------------|--------------------|
 * | no `shouldShowInPrimary`             | no                  | yes                |
 * | `shouldShowInPrimary` returns true   | no                  | yes                |
 * | `shouldShowInPrimary` returns false  | yes                 | no                 |
 */

/**
 * Determines whether an action should appear in the primary menu.
 *
 * - Actions without `shouldShowInPrimary` → always in primary
 * - Actions with `shouldShowInPrimary` → in primary when the predicate returns true
 *   (use `() => false` for actions that should always be nested)
 */
const shouldShowInPrimary = ({
	action,
	api,
	getEditorView,
}: {
	action: BlockMenuActionDef;
	api: ExtractInjectionAPI<AIPlugin>;
	getEditorView: () => EditorView | undefined;
}): boolean => {
	if (!action.shouldShowInPrimary) {
		return true;
	}
	const context = getSelectionContext({ api, getEditorView });
	if (!context) {
		return true;
	}
	return action.shouldShowInPrimary(context);
};

export const createAIBlockMenuComponents = ({
	editorPluginAIProvider,
	getEditorView,
	api,
}: {
	api: ExtractInjectionAPI<AIPlugin>;
	editorPluginAIProvider: EditorPluginAIProvider;
	getEditorView: () => EditorView | undefined;
}): RegisterBlockMenuComponent[] => {
	const actions = getBlockMenuActions({ editorPluginAIProvider });
	const nestedActions = actions.filter((action) => action.shouldShowInPrimary);

	return [
		// 1. AI section
		{
			type: 'block-menu-section',
			key: AI_MENU_SECTION.key,
			rank: MAIN_BLOCK_MENU_SECTION_RANK[AI_MENU_SECTION.key],
			component: ({ children }: { children: React.ReactNode }) => (
				<ToolbarDropdownItemSection hasSeparator={true}>{children}</ToolbarDropdownItemSection>
			),
		},
		// 2. Primary items — all actions, hidden when shouldShowInPrimary returns false
		...actions.map((action, index) => ({
			type: 'block-menu-item' as const,
			key: `ai-${action.key}`,
			parent: {
				type: 'block-menu-section' as const,
				key: AI_MENU_SECTION.key,
				rank: AI_MENU_SECTION_RANK[AI_MENU_SECTION.key],
			},
			rank: index,
			isHidden: () => !shouldShowInPrimary({ action, api, getEditorView }),
			component: () => (
				<BlockMenuActionItem
					action={action}
					api={api}
					editorPluginAIProvider={editorPluginAIProvider}
					getEditorView={getEditorView}
				/>
			),
		})),
		// 3. Nested menu — auto-renders when it has ≥1 visible child
		...(nestedActions.length > 0
			? [
					{
						type: 'block-menu-nested' as const,
						key: AI_NESTED_MENU_KEY.key,
						parent: {
							type: 'block-menu-section' as const,
							key: AI_MENU_SECTION.key,
							rank: AI_MENU_SECTION_RANK[AI_NESTED_MENU_KEY.key],
						},
						component: ({ children }: { children: React.ReactNode }) => (
							<ShowMoreMenu>{children}</ShowMoreMenu>
						),
					},
					{
						type: 'block-menu-section' as const,
						rank: AI_MENU_SECTION_RANK[AI_NESTED_SECTION_KEY.key],
						key: AI_NESTED_SECTION_KEY.key,
						parent: {
							type: 'block-menu-nested' as const,
							key: AI_NESTED_MENU_KEY.key,
							rank: AI_MENU_SECTION_RANK[AI_NESTED_MENU_KEY.key],
						},
						component: ({ children }: { children: React.ReactNode }) => (
							<ToolbarDropdownItemSection>{children}</ToolbarDropdownItemSection>
						),
					},
					// 4. Nested items — actions with shouldShowInPrimary, shown when NOT in primary
					...nestedActions.flatMap((action, index) => {
						// Actions with children render as a nested sub-menu
						if (action.children && action.children.length > 0) {
							const nestedKey = `ai-nested-${action.key}`;
							const nestedSectionKey = `ai-nested-section-${action.key}`;

							return [
								// Nested menu trigger (e.g., "Change tone >")
								{
									type: 'block-menu-nested' as const,
									key: nestedKey,
									parent: {
										type: 'block-menu-section' as const,
										key: AI_NESTED_SECTION_KEY.key,
										rank: index,
									},
									component: ({ children }: { children: React.ReactNode } = { children: null }) => (
										<NestedActionMenu action={action}>{children}</NestedActionMenu>
									),
								},
								// Section inside the nested menu
								{
									type: 'block-menu-section' as const,
									key: nestedSectionKey,
									parent: {
										type: 'block-menu-nested' as const,
										key: nestedKey,
										rank: 0,
									},
									component: ({ children }: { children: React.ReactNode } = { children: null }) => (
										<ToolbarDropdownItemSection>{children}</ToolbarDropdownItemSection>
									),
								},
								// Child items inside the nested section
								...action.children.map((child, childIndex) => ({
									type: 'block-menu-item' as const,
									key: `ai-nested-${child.key}`,
									parent: {
										type: 'block-menu-section' as const,
										key: nestedSectionKey,
										rank: childIndex,
									},
									rank: childIndex,
									component: () => (
										<BlockMenuActionItem
											action={child}
											api={api}
											editorPluginAIProvider={editorPluginAIProvider}
											getEditorView={getEditorView}
										/>
									),
								})),
							];
						}

						// Regular nested menu items
						return [
							{
								type: 'block-menu-item' as const,
								key: action.key,
								parent: {
									type: 'block-menu-section' as const,
									key: AI_NESTED_SECTION_KEY.key,
									rank: index,
								},
								rank: index,
								isHidden: () => shouldShowInPrimary({ action, api, getEditorView }),
								component: () => (
									<BlockMenuActionItem
										action={action}
										api={api}
										editorPluginAIProvider={editorPluginAIProvider}
										getEditorView={getEditorView}
									/>
								),
							},
						];
					}),
				]
			: []),
	];
};
